import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface InviteColleagueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId: string;
  channelName: string;
}

export const InviteColleagueDialog = ({
  open,
  onOpenChange,
  channelId,
  channelName,
}: InviteColleagueDialogProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [remainingInvites, setRemainingInvites] = useState<number | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !consentChecked) {
      toast({
        title: "Missing information",
        description: "Please fill in the email and accept the privacy notice",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-invitation", {
        body: {
          invitee_email: email,
          invitee_name: name || undefined,
          channel_id: channelId,
          channel_name: channelName,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      setRemainingInvites(data.remaining_invites);

      toast({
        title: "Invitation sent!",
        description: data.is_unlimited 
          ? `Successfully sent invitation to ${email}. You have unlimited invitations as an iSPAI team member.`
          : `Successfully sent invitation to ${email}. You have ${data.remaining_invites} invitation${data.remaining_invites === 1 ? '' : 's'} remaining.`,
      });

      setEmail("");
      setName("");
      setConsentChecked(false);
      onOpenChange(false);
    } catch (error: any) {
      // Invitation error handled by toast notification
      toast({
        title: "Failed to send invitation",
        description: error.message || "An error occurred while sending the invitation",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite a Colleague</DialogTitle>
          <DialogDescription>
            Send a personal invitation to join {channelName}
            {remainingInvites !== null && (
              <span className="block mt-2 font-medium text-primary">
                {remainingInvites >= 999 
                  ? "You have unlimited invitations as an iSPAI team member"
                  : `You have ${remainingInvites} invitation${remainingInvites === 1 ? '' : 's'} remaining in 2025`
                }
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="colleague@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name (optional)</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <p className="text-sm font-medium">Privacy Notice</p>
            <p className="text-xs text-muted-foreground">
              During our MVP phase, iSPAI tracks user engagement metrics to improve UX. 
              Please advise invitees to avoid sharing private IP or confidential content.
            </p>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="consent"
              checked={consentChecked}
              onCheckedChange={(checked) => setConsentChecked(checked as boolean)}
            />
            <label
              htmlFor="consent"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I acknowledge the privacy notice and will inform the invitee about data collection practices
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !consentChecked}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Invitation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
